/* eslint-disable react/prop-types */
import { useRef, useState } from "react";
import { ImagePlus, Loader2, Globe, X, Check } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";
import { uploadFile, uploadUrl } from "../../lib/uploads";

/** Customer logo on the profile: upload, remove, or pick one found on their website. */
export default function CustomerLogo({ customer, canEdit, onChanged, size = "h-16 w-16" }) {
  const [busy, setBusy] = useState(false);
  const [suggest, setSuggest] = useState(null);
  const ref = useRef(null);
  const cid = customer.id;
  const save = async (upload_id) => {
    setBusy(true);
    try {
      await api.put(`/customers/${cid}/logo`, { upload_id });
      setSuggest(null);
      toast.success(upload_id ? "Logo saved" : "Logo removed");
      onChanged?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const up = await uploadFile(f, { target_kind: "customer", target_id: cid, label: f.name });
      await save(up.id);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); setBusy(false); }
  };
  const find = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/customers/${cid}/logo/suggest`, { website: customer.website || null });
      setSuggest(data);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const logo = customer.logo_upload_id;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="customer-logo-block">
      <div className={`${size} rounded-lg bg-white border border-black/10 flex items-center justify-center overflow-hidden shrink-0`}>
        {logo ? <img src={uploadUrl(logo)} alt="logo" className="w-full h-full object-contain p-1" data-testid="customer-logo-img" />
          : <ImagePlus className="w-5 h-5 text-slate-400" />}
      </div>
      {canEdit && !suggest && (
        <div className="flex flex-col gap-1 text-[11px]">
          <button type="button" disabled={busy} onClick={() => ref.current?.click()} className="hv-btn-ghost px-2 py-0.5 rounded border border-white/10 text-left" data-testid="customer-logo-upload-btn">
            {busy ? <Loader2 className="w-3 h-3 inline animate-spin mr-1" /> : null}{logo ? "Replace logo" : "Upload logo"}
          </button>
          {!logo && (
            <button type="button" disabled={busy} onClick={find} className="hv-btn-ghost px-2 py-0.5 rounded border border-white/10 text-left" data-testid="customer-logo-find-btn">
              <Globe className="w-3 h-3 inline mr-1" />Find on their website
            </button>
          )}
          {logo && (
            <button type="button" disabled={busy} onClick={() => save(null)} className="px-2 py-0.5 rounded text-left text-rose-400 hover:underline" data-testid="customer-logo-remove-btn">Remove</button>
          )}
        </div>
      )}
      {suggest && (
        <div className="flex items-center gap-2 rounded-lg border border-cyan-400/40 p-2" data-testid="customer-logo-suggestion">
          <div className="h-14 w-20 bg-white rounded flex items-center justify-center">
            <img src={uploadUrl(suggest.upload_id)} alt="found logo" className="max-h-full max-w-full object-contain" data-testid="customer-logo-suggestion-img" />
          </div>
          <div className="text-[11px] space-y-1">
            <div>Use this logo from their website?</div>
            <div className="flex gap-1">
              <button type="button" disabled={busy} onClick={() => save(suggest.upload_id)} className="px-2 py-0.5 rounded bg-cyan-600 text-white" data-testid="customer-logo-suggestion-accept"><Check className="w-3 h-3 inline mr-0.5" />Use it</button>
              <button type="button" onClick={() => setSuggest(null)} className="px-2 py-0.5 rounded border border-white/15" data-testid="customer-logo-suggestion-reject"><X className="w-3 h-3 inline mr-0.5" />No</button>
            </div>
          </div>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="customer-logo-file" />
    </div>
  );
}
